import React, { useMemo } from 'react';
import { Bot, User } from 'lucide-react';
import { palette } from '../utils/designTokens';
import { truncateText } from '../utils/traceIoParser';
import { getTraceSignalBadges } from '../utils/signalUtils';

const SignalBadge = ({ label, variant = 'neutral' }) => {
  const variants = {
    danger: 'text-danger bg-danger/10',
    warning: 'text-warning bg-warning/10',
    info: 'text-accent-primary-base bg-accent-primary-base/10',
    neutral: 'text-text-muted bg-background-tertiary',
    warm: 'text-accent-warm-base bg-accent-warm-base/10',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong ${variants[variant] || variants.neutral}`}>
      {label}
    </span>
  );
};

/**
 * ChatTurn - Renders a single user/bot conversation turn from a trace.
 *
 * @param {Object}   trace            - Trace object with input/output fields
 * @param {boolean}  isSelected       - Whether this turn is currently selected
 * @param {Function} onSelect         - Callback receiving trace_id on click
 * @param {boolean}  compact          - If true, uses smaller text and shorter truncation
 * @param {number}   participantCount - Number of services involved (>1 = cross-service)
 */
const ChatTurn = ({ trace, isSelected, onSelect, compact = false, participantCount = 1 }) => {
  const maxLen = compact ? 120 : 240;
  const textSize = compact ? 'text-xs' : 'text-sm';
  const iconSize = compact ? 12 : 16;
  const padding = compact ? 'px-2 py-1.5' : 'px-3 py-2';
  const bubblePadding = compact ? 'px-2 py-1' : 'px-3 py-2';

  const inputText = useMemo(() => trace.input || null, [trace.input]);
  const outputText = useMemo(() => trace.output || null, [trace.output]);
  const traceSignals = useMemo(() => getTraceSignalBadges(trace), [trace]);

  return (
    <button
      type="button"
      onClick={() => onSelect(trace.trace_id)}
      className={`w-full text-left rounded-lg border ${padding} transition-colors ${isSelected ? 'border-accent-primary-base border-l-[3px]' : 'bg-background-secondary border-border-subtle hover:bg-background-tertiary'}`}
      style={isSelected ? { backgroundColor: palette.selection.bg } : undefined}
      aria-pressed={isSelected}
    >
      <div className="space-y-1.5">
        {/* User input message - left-aligned */}
        <div className="flex justify-start">
          <div className={`max-w-[85%] overflow-hidden rounded-2xl rounded-tl-sm border border-border-strong bg-background-quaternary ${bubblePadding}`}>
            <div className="flex items-start gap-1.5">
              <User size={iconSize} className="shrink-0 mt-0.5" style={{ color: palette.accents.primary.base }} />
              <div className={`${textSize} text-text-primary whitespace-pre-wrap break-words min-w-0`} style={{ overflowWrap: 'anywhere' }}>
                {truncateText(inputText, maxLen)}
              </div>
            </div>
          </div>
        </div>

        {/* Assistant output message - right-aligned */}
        <div className="flex justify-end">
          <div className={`max-w-[85%] overflow-hidden rounded-2xl rounded-tr-sm border border-accent-primary-base/60 bg-accent-primary-base shadow-sm ${bubblePadding}`}>
            <div className="flex items-start gap-1.5 justify-end">
              <Bot size={iconSize} className="shrink-0 mt-0.5" style={{ color: palette.text.inverse }} />
              <div className={`${textSize} text-text-inverse whitespace-pre-wrap break-words text-left min-w-0`} style={{ overflowWrap: 'anywhere' }}>
                {truncateText(outputText, maxLen)}
              </div>
            </div>
          </div>
        </div>

        {(traceSignals.length > 0 || participantCount > 1) && (
          <div className="mt-1 flex flex-wrap gap-1.5 justify-end">
            {participantCount > 1 && (
              <SignalBadge label={`${participantCount} services`} variant="warm" />
            )}
            {traceSignals.map((signal, idx) => (
              <SignalBadge key={`${signal.label}-${idx}`} label={signal.label} variant={signal.variant} />
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

export default ChatTurn;
